import { Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { DataTableAction } from "./data-table-header";
import { FacetedFilter } from "./faceted-filter";

export interface DataTableFilter {
  label: string;
  value: string;
}

export interface DataTableFacet {
  column: string;
  title: string;
  options: { label: string; value: string }[];
}

interface DataTableToolbarProps {
  filters?: DataTableFilter[];
  activeFilter?: string;
  onFilterChange?: (value: string) => void;
  filterVariant?: "tabs" | "dropdown";
  facets?: DataTableFacet[];
  activeFacets?: Record<string, string[]>;
  onFacetChange?: (column: string, values: string[]) => void;
  search?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  selectedCount?: number;
  bulkActions?: DataTableAction[];
  filteredRowCount?: number;
  isAllFilteredSelected?: boolean;
  onSelectAllFiltered?: () => void;
  onClearSelection?: () => void;
}

export function DataTableToolbar({
  filters,
  activeFilter,
  onFilterChange,
  filterVariant = "tabs",
  facets,
  activeFacets = {},
  onFacetChange,
  search,
  onSearchChange,
  searchPlaceholder = "Search...",
  selectedCount = 0,
  bulkActions,
  filteredRowCount,
  isAllFilteredSelected = false,
  onSelectAllFiltered,
  onClearSelection,
}: DataTableToolbarProps) {
  const hasActiveFacets = facets?.some((f) => (activeFacets[f.column]?.length ?? 0) > 0);

  const handleReset = () => {
    for (const f of facets ?? []) {
      onFacetChange?.(f.column, []);
    }
  };

  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-2 flex-wrap">
        {/* Faceted filters */}
        {facets &&
          onFacetChange &&
          facets.map((facet) => (
            <FacetedFilter
              key={facet.column}
              title={facet.title}
              options={facet.options}
              selected={activeFacets[facet.column] ?? []}
              onChange={(values) => onFacetChange(facet.column, values)}
            />
          ))}

        {/* Reset button */}
        {hasActiveFacets && (
          <Button variant="ghost" size="sm" className="h-8 px-2" onClick={handleReset}>
            Reset
            <X className="ml-1 h-4 w-4" />
          </Button>
        )}

        {/* Legacy: tab-style filters */}
        {filters && filters.length > 0 && onFilterChange && filterVariant === "tabs" && (
          <Tabs value={activeFilter || filters[0]?.value} onValueChange={onFilterChange}>
            <TabsList>
              {filters.map((filter) => (
                <TabsTrigger key={filter.value} value={filter.value}>
                  {filter.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        )}

        {/* Legacy: dropdown-style filter */}
        {filters && filters.length > 0 && onFilterChange && filterVariant === "dropdown" && (
          <Select value={activeFilter || filters[0]?.value} onValueChange={onFilterChange}>
            <SelectTrigger className="h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {filters.map((filter) => (
                <SelectItem key={filter.value} value={filter.value}>
                  {filter.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Bulk actions */}
        {selectedCount > 0 && bulkActions && bulkActions.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">{selectedCount} selected</span>
            {!isAllFilteredSelected &&
              onSelectAllFiltered &&
              typeof filteredRowCount === "number" &&
              filteredRowCount > selectedCount && (
                <Button variant="ghost" size="sm" onClick={onSelectAllFiltered}>
                  Select all {filteredRowCount} filtered
                </Button>
              )}
            {bulkActions.map((action) => (
              <Button
                key={action.label}
                variant={action.variant || "outline"}
                size="sm"
                onClick={action.onClick}
                disabled={action.disabled}
              >
                {action.icon && <action.icon className="mr-2 h-4 w-4" />}
                {action.label}
              </Button>
            ))}
            {onClearSelection && (
              <Button variant="ghost" size="sm" onClick={onClearSelection}>
                Clear
              </Button>
            )}
          </div>
        )}
      </div>

      {onSearchChange && (
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={searchPlaceholder}
            value={search || ""}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9"
          />
        </div>
      )}
    </div>
  );
}
